import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Document, DocumentType } from '../types';
import { getFinancialDocuments } from '../utils/financialUtils';

import { Filter, Calendar, DollarSign, Wallet, Copy, Check, CheckCircle, Clock } from 'lucide-react';
import { formatDate } from '../utils/formatters';
import { api } from '../services/api';

interface ClientFinancialsTableProps {
    documents: Document[];
    documentTypes: DocumentType[];
    dateFormat?: string;
    onRefresh?: () => void;
}

export const ClientFinancialsTable: React.FC<ClientFinancialsTableProps> = ({ documents, documentTypes, dateFormat, onRefresh }) => {
    const { t, i18n } = useTranslation(['bi']);

    const [selectedMonth, setSelectedMonth] = useState<string>('all');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [selectedStatus, setSelectedStatus] = useState<string>('all');

    const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
    const [localStatusOverrides, setLocalStatusOverrides] = useState<Record<string, 'paid' | 'pending'>>({});

    // Function to handle status update
    const handleStatusToggle = async (doc: Document) => {
        if (updatingIds.has(doc.id)) return;

        const currentStatus = localStatusOverrides[doc.id] ?? doc.paymentStatus ?? 'pending';
        const newStatus = currentStatus === 'paid' ? 'pending' : 'paid';

        // Optimistic Update
        setLocalStatusOverrides(prev => ({ ...prev, [doc.id]: newStatus }));

        try {
            setUpdatingIds(prev => new Set(prev).add(doc.id));
            await api.updateDocumentPaymentStatus(doc.id, newStatus);
            if (onRefresh) onRefresh();
        } catch (e) {
            console.error("Status Update Failed", e);
            alert("Failed to update status");
            // Revert optimistic update on failure
            setLocalStatusOverrides(prev => {
                const newState = { ...prev };
                delete newState[doc.id];
                return newState;
            });
        } finally {
            setUpdatingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(doc.id);
                return newSet;
            });
        }
    };


    // Base Financial Docs (Shared Logic)
    const baseDocs = useMemo(() => getFinancialDocuments(documents, documentTypes), [documents, documentTypes]);

    // Extract available months from documents for the filter (Using Due Date / Installment Date)
    const availableMonths = useMemo(() => {
        const months = new Set<string>();
        baseDocs.forEach(doc => {
            const dueDate = doc.ai_analysis?.installment_info?.installment_date;
            if (dueDate) {
                const date = new Date(dueDate);
                const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                months.add(key);
            }
        });
        return Array.from(months).sort().reverse();
    }, [baseDocs]);

    // Extract available categories
    const availableCategories = useMemo(() => {
        const categories = new Set<string>();
        baseDocs.forEach(doc => {
            if (doc.typeName) {
                categories.add(doc.typeName);
            }
        });
        return Array.from(categories).sort();
    }, [baseDocs]);

    // Filter documents
    const filteredDocs = useMemo(() => {
        return baseDocs.filter(doc => {
            // Month Filter (Based on Due Date)
            if (selectedMonth !== 'all') {
                const dueDate = doc.ai_analysis?.installment_info?.installment_date;
                if (!dueDate) return false; // If selected a specific month, docs without due date are hidden

                const date = new Date(dueDate);
                const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                if (key !== selectedMonth) return false;
            }

            // Category Filter
            if (selectedCategory !== 'all' && doc.typeName !== selectedCategory) {
                return false;
            }

            // Status Filter
            if (selectedStatus !== 'all') {
                const currentStatus = localStatusOverrides[doc.id] ?? doc.paymentStatus ?? 'pending';
                if (selectedStatus !== currentStatus) return false;
            }

            return true;
        });
    }, [baseDocs, selectedMonth, selectedCategory, selectedStatus, localStatusOverrides]);

    // Format Currency
    const formatCurrency = (amount: number | undefined | null) => {
        if (amount === undefined || amount === null) return '-';
        return new Intl.NumberFormat(i18n.language === 'el' ? 'el-GR' : 'en-US', { style: 'currency', currency: 'EUR' }).format(amount);
    };

    // Format Date (Period)
    const formatPeriod = (dateStr: string | null | undefined) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString(i18n.language === 'el' ? 'el-GR' : 'en-US', { month: 'long', year: 'numeric' });
    };

    if (documents.length === 0) return null;

    return (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mt-8">
            <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <Wallet size={20} className="text-blue-600" />
                        {t('bi:financial_overview.title')}
                    </h2>
                    <p className="text-sm text-slate-500">{t('bi:financial_overview.subtitle')}</p>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-4">
                    {/* Category Filter */}
                    <div className="flex items-center gap-2">
                        <Filter size={16} className="text-slate-400" />
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 outline-none capitalize"
                        >
                            <option value="all">{t('bi:financial_overview.filters.all_categories')}</option>
                            {availableCategories.map(cat => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>

                    </div>

                    {/* Status Filter */}
                    <div className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-slate-400" />
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 outline-none capitalize"
                        >
                            <option value="all">{t('bi:financial_overview.filters.all_statuses')}</option>
                            <option value="paid">{t('bi:financial_overview.status.paid')}</option>
                            <option value="pending">{t('bi:financial_overview.status.pending')}</option>
                        </select>
                    </div>

                    {/* Month Filter */}
                    <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-slate-400" />
                        <select
                            value={selectedMonth}
                            onChange={(e) => setSelectedMonth(e.target.value)}
                            className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2 outline-none"
                        >
                            <option value="all">{t('bi:financial_overview.filters.all_months')}</option>
                            {availableMonths.map(month => (
                                <option key={month} value={month}>
                                    {new Date(month).toLocaleDateString(i18n.language === 'el' ? 'el-GR' : 'en-US', { month: 'long', year: 'numeric' })}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left text-slate-500">
                    <thead className="text-xs text-slate-700 uppercase bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="px-6 py-4">{t('bi:financial_overview.headers.document')}</th>
                            <th className="px-6 py-4">{t('bi:financial_overview.headers.category')}</th>
                            <th className="px-6 py-4">{t('bi:financial_overview.headers.issuer')}</th>
                            <th className="px-6 py-4">{t('bi:financial_overview.headers.period')}</th>
                            <th className="px-6 py-4 text-center">{t('bi:financial_overview.headers.payment_code')}</th>
                            <th className="px-6 py-4 text-right">{t('bi:financial_overview.headers.total_debt')}</th>
                            <th className="px-6 py-4 text-right">{t('bi:financial_overview.headers.installment')}</th>
                            <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                {t('bi:financial_overview.headers.status')}
                            </th>
                            <th className="px-6 py-4 text-center">{t('bi:financial_overview.headers.due_date')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDocs.length > 0 ? (
                            filteredDocs.map((doc) => (
                                <tr key={doc.id} className="bg-white border-b border-slate-100 hover:bg-slate-50 transition">
                                    <td className="px-6 py-4 font-medium text-slate-900 max-w-xs" title={doc.title || 'Untitled'}>
                                        <a
                                            href={doc.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline flex items-center gap-1"
                                        >
                                            <span className="truncate">{doc.title || 'Untitled'}</span>
                                        </a>
                                    </td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-gray-100 text-gray-800`}>
                                            {doc.typeName || 'Unknown'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 font-medium text-slate-900">
                                        {doc.ai_analysis?.details.issuer || '-'}
                                    </td>
                                    <td className="px-6 py-4">
                                        {formatPeriod(doc.ai_analysis?.doc_date)}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {doc.ai_analysis?.installment_info?.payment_code ? (
                                            <PaymentCodeButton code={doc.ai_analysis.installment_info.payment_code} label={t('bi:financial_overview.actions.pay')} copiedLabel={t('bi:financial_overview.actions.copied')} />
                                        ) : '-'}
                                    </td>
                                    <td className="px-6 py-4 text-right font-bold text-slate-700">
                                        {formatCurrency(doc.ai_analysis?.installment_info?.total_debt_amount)}
                                    </td>
                                    <td className="px-6 py-4 text-right font-bold text-blue-600">
                                        {formatCurrency(doc.ai_analysis?.installment_info?.installment_amount || doc.ai_analysis?.installment_info?.total_debt_amount)}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {(() => {
                                            const status = localStatusOverrides[doc.id] ?? doc.paymentStatus ?? 'pending';
                                            const isPaid = status === 'paid';
                                            return (
                                                <button
                                                    onClick={() => handleStatusToggle(doc)}
                                                    disabled={updatingIds.has(doc.id)}
                                                    className={`
                                                inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all
                                                ${isPaid
                                                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                            : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}
                                                ${updatingIds.has(doc.id) ? 'opacity-50 cursor-wait' : ''}
                                            `}
                                                >
                                                    {isPaid ? <CheckCircle size={14} /> : <Clock size={14} />}
                                                    {isPaid ? t('bi:financial_overview.status.paid') : t('bi:financial_overview.status.pending')}
                                                </button>
                                            );
                                        })()}
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        {doc.ai_analysis?.installment_info?.installment_date ? (
                                            <div className="flex items-center justify-center gap-1 text-slate-600">
                                                <Calendar size={14} />
                                                {formatDate(doc.ai_analysis.installment_info.installment_date, dateFormat)}
                                            </div>
                                        ) : '-'}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={8} className="px-6 py-8 text-center text-slate-400">
                                    {t('bi:financial_overview.no_records')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {/* Summary Footer */}
                    {filteredDocs.length > 0 && (
                        <tfoot className="bg-slate-50 border-t-2 border-slate-200 font-bold text-slate-900">
                            <tr>
                                <td colSpan={5} className="px-6 py-4 text-right text-xs uppercase tracking-wider text-slate-500">
                                    {t('bi:financial_overview.totals')}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    {formatCurrency(filteredDocs.reduce((sum, doc) => sum + (doc.ai_analysis?.installment_info?.total_debt_amount || 0), 0))}
                                </td>
                                <td className="px-6 py-4 text-right text-blue-700">
                                    {formatCurrency(filteredDocs.reduce((sum, doc) => sum + (doc.ai_analysis?.installment_info?.installment_amount || doc.ai_analysis?.installment_info?.total_debt_amount || 0), 0))}
                                </td>
                                <td className="px-6 py-4"></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div >
    );
};

const PaymentCodeButton = ({ code, label, copiedLabel }: { code: string, label: string, copiedLabel: string }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code.replace(/\s/g, '')); // Copy without spaces
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="relative group inline-block">
            <button
                onClick={handleCopy}
                className={`p-2 rounded-lg transition-colors flex items-center gap-2 ${copied ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600'}`}
            >
                {copied ? <Check size={16} /> : <Copy size={16} />}
                <span className="text-xs font-bold">{copied ? copiedLabel : label}</span>
            </button>

            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max max-w-[200px] px-2 py-1 bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 shadow-lg whitespace-normal break-all">
                {code}
                <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-slate-800"></div>
            </div>
        </div>
    );
};
